import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Button, Tooltip, Typography } from '@mui/material';
import { generateSuppressedFieldsPath } from 'app/services/loanNavigation';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { PAGE_CODES } from 'constants/pageCodes';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  changeApplicationChecklistResultAction,
  optimisticallyOverrideChecklistItemAction,
} from 'store/loanApplications/actions';
import { getApplicationChecklistByIdAction } from 'store/loanApplications/actions/checklists.actions';

import {
  indicatorStyles,
  neededBadgeStyles,
  neededIconStyles,
  overriddenContainerStyles,
  suppressButtonStyles,
} from './FieldCompletenessIndicator.styles';
import { FieldCompletenessIndicatorTestIds } from './FieldCompletenessIndicator.testIds';

/**
 * Overlay indicator injected via FieldPresenceWrapper when a checklist item
 * is failing/incomplete and linked to this field via `fieldRef` (DEV-4383).
 *
 * When NOT overridden: shows a purple "! Needed" badge + a quiet "Not needed"
 * suppress button. Clicking suppresses the checklist item via an override action.
 *
 * When overridden: shows an orange "Overridden" chip (no purple highlight).
 */
const FieldCompletenessIndicator = ({
  checklistId,
  itemId,
  isOverridden,
  targetId,
  targetType,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const history = useHistory();

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS));

  const refetchChecklist = useCallback(
    () =>
      dispatch(
        getApplicationChecklistByIdAction({
          applicationId,
          checklistId,
          includeResults: true,
          includeItems: true,
          includePendingChanges: true,
        }),
      ),
    [dispatch, applicationId, checklistId],
  );

  const onUndoSuppress = useCallback(() => {
    dispatch(
      optimisticallyOverrideChecklistItemAction({
        checklistId,
        itemId,
        targetId,
        isOverridden: false,
      }),
    );

    dispatch(
      changeApplicationChecklistResultAction({
        itemsToOverride: [],
        itemsToRemoveOverride: [itemId],
        checklistId,
        applicationId,
        targetId,
        targetType,
      }),
    ).then(refetchChecklist);
  }, [dispatch, applicationId, checklistId, itemId, targetId, targetType, refetchChecklist]);

  const onViewSuppressed = useCallback(() => {
    history.push(generateSuppressedFieldsPath({ applicationId, checklistId, itemId }));
  }, [history, applicationId, checklistId, itemId]);

  const onMarkNotNeeded = useCallback(
    e => {
      e.stopPropagation();

      // Optimistic flip so the indicator shows "Overridden" on the click — the
      // server re-eval is async and the truth arrives later via the SignalR
      // snapshot push (DEV-4468). `targetId` (the entry's exact snapshot target — a
      // borrower id for a per-borrower field) disambiguates a Borrower-scoped item from a
      // sibling borrower's snapshot sharing the same checklistId (DEV-4684).
      dispatch(optimisticallyOverrideChecklistItemAction({ checklistId, itemId, targetId }));

      dispatch(
        showMessageAction({
          variant: 'success',
          message: "Field marked not needed. It's now listed under Alerts › Suppressed fields.",
          actions: [
            { id: 'view', label: 'View', onClick: onViewSuppressed },
            { id: 'undo', label: 'Undo', onClick: onUndoSuppress },
          ],
        }),
      );

      dispatch(
        changeApplicationChecklistResultAction({
          itemsToOverride: [itemId],
          itemsToRemoveOverride: [],
          checklistId,
          applicationId,
          // DEV-4728: record the override against the item's own snapshot target — the borrower
          // for a per-borrower field — not the loan-wide LoanApplication target (which never
          // matched the Borrower snapshot, so the suppression silently reverted on reload).
          targetId,
          targetType,
        }),
      ).then(refetchChecklist);
    },
    [
      dispatch,
      applicationId,
      checklistId,
      itemId,
      targetId,
      targetType,
      refetchChecklist,
      onViewSuppressed,
      onUndoSuppress,
    ],
  );

  if (isOverridden) {
    return (
      <Box sx={indicatorStyles}>
        <Box sx={overriddenContainerStyles}>
          <LabelCDS size={LabelSize.S} title="Overridden" variant={LabelVariant.ORANGE} />
        </Box>
      </Box>
    );
  }

  return (
    <Tooltip title="This field is expected for this loan">
      <Box sx={indicatorStyles}>
        <Box sx={neededBadgeStyles}>
          <Box component="span" sx={neededIconStyles}>
            <Typography component="span" variant="medium12">
              !
            </Typography>
          </Box>
          <Typography variant="medium12">Needed</Typography>
        </Box>
        {isManageAllowed && (
          <Button
            disableRipple
            data-testid={FieldCompletenessIndicatorTestIds.SUPPRESS_BUTTON}
            size="small"
            sx={suppressButtonStyles}
            type="button"
            variant="text"
            onClick={onMarkNotNeeded}
          >
            Not needed
          </Button>
        )}
      </Box>
    </Tooltip>
  );
};

FieldCompletenessIndicator.propTypes = {
  checklistId: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  isOverridden: PropTypes.bool.isRequired,
  targetId: PropTypes.string,
  targetType: PropTypes.string,
};

export default memo(FieldCompletenessIndicator);
